import React from "react";
import {
  RowSelectField,
  RowTextField,
  RowTextAreaField,
} from "./MultiRowFields";
import {
  DIET_TYPE_OPTIONS,
  FOOD_TEXTURE_OPTIONS,
  FLUID_MANAGEMENT_OPTIONS,
  MEAL_ASSISTANCE_OPTIONS,
  SUPPLEMENT_ENTERAL_OPTIONS,
  SODIUM_RESTRICTION_OPTIONS,
  CARBOHYDRATE_RESTRICTION_OPTIONS,
  buildNutrition,
  formatDietType,
  formatSupplementEnteral,
  formatSodiumRestriction,
  formatCarbohydrateRestriction,
  hasDietData,
  hasRestrictionData,
} from "../patientNutrition.shared";

const SubsectionTitle = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
    {children}
  </p>
);

const ViewField = ({ label, value, className = "" }) => (
  <div className={`min-w-0 py-0.5 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-slate-900 font-medium break-words leading-snug whitespace-pre-wrap">
      {value || "—"}
    </div>
  </div>
);

export const NutritionDietEditFields = ({ nutrition, onChange }) => {
  const set = (key) => (e) =>
    onChange((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
      <RowSelectField
        label="Diet Type"
        value={nutrition.dietType}
        onChange={set("dietType")}
        options={DIET_TYPE_OPTIONS}
        placeholder="Select diet type"
      />
      {nutrition.dietType === "Other" ? (
        <RowTextField
          label="Diet Type (Other)"
          value={nutrition.dietTypeOther}
          onChange={set("dietTypeOther")}
          placeholder="Specify diet type"
          required
        />
      ) : null}
      <RowSelectField
        label="Food Texture"
        value={nutrition.foodTexture}
        onChange={set("foodTexture")}
        options={FOOD_TEXTURE_OPTIONS}
        placeholder="Select texture"
      />
      <RowSelectField
        label="Fluid Management"
        value={nutrition.fluidManagement}
        onChange={set("fluidManagement")}
        options={FLUID_MANAGEMENT_OPTIONS}
        placeholder="Select fluid plan"
      />
      <RowSelectField
        label="Meal Assistance"
        value={nutrition.mealAssistance}
        onChange={set("mealAssistance")}
        options={MEAL_ASSISTANCE_OPTIONS}
        placeholder="Select assistance level"
      />
      <RowSelectField
        label="Supplement / Enteral"
        value={nutrition.supplementEnteral}
        onChange={set("supplementEnteral")}
        options={SUPPLEMENT_ENTERAL_OPTIONS}
        placeholder="Select if applicable"
      />
      {nutrition.supplementEnteral === "Other" ? (
        <RowTextField
          label="Supplement / Enteral (Other)"
          value={nutrition.supplementOther}
          onChange={set("supplementOther")}
          placeholder="Specify supplement or tube feeding"
          required
        />
      ) : null}
      <RowTextAreaField
        label="Diet Instructions"
        value={nutrition.dietInstructions}
        onChange={set("dietInstructions")}
        placeholder="Special diet orders, preferences, meal timing, aspiration precautions…"
        className="sm:col-span-2 lg:col-span-3"
        rows={3}
      />
    </div>
  );
};

export const NutritionRestrictionEditFields = ({ nutrition, onChange }) => {
  const set = (key) => (e) =>
    onChange((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
      <RowSelectField
        label="Sodium Restriction"
        value={nutrition.sodiumRestriction}
        onChange={set("sodiumRestriction")}
        options={SODIUM_RESTRICTION_OPTIONS}
        placeholder="Select sodium level"
      />
      {nutrition.sodiumRestriction === "Other" ? (
        <RowTextField
          label="Sodium Restriction (Other)"
          value={nutrition.sodiumRestrictionDetail}
          onChange={set("sodiumRestrictionDetail")}
          placeholder="Specify sodium restriction"
          required
        />
      ) : (
        <RowTextField
          label="Sodium Detail"
          value={nutrition.sodiumRestrictionDetail}
          onChange={set("sodiumRestrictionDetail")}
          placeholder="e.g. 1800 mg/day per physician order"
        />
      )}
      <RowTextField
        label="Fluid Restriction"
        value={nutrition.fluidRestriction}
        onChange={set("fluidRestriction")}
        placeholder="e.g. 1500 ml/day or None"
      />
      <RowSelectField
        label="Carbohydrate / Sugar"
        value={nutrition.carbohydrateRestriction}
        onChange={set("carbohydrateRestriction")}
        options={CARBOHYDRATE_RESTRICTION_OPTIONS}
        placeholder="Select if applicable"
      />
      {nutrition.carbohydrateRestriction === "Other" ? (
        <RowTextField
          label="Carbohydrate (Other)"
          value={nutrition.carbohydrateRestrictionDetail}
          onChange={set("carbohydrateRestrictionDetail")}
          placeholder="Specify carbohydrate restriction"
          required
        />
      ) : (
        <RowTextField
          label="Carbohydrate Detail"
          value={nutrition.carbohydrateRestrictionDetail}
          onChange={set("carbohydrateRestrictionDetail")}
          placeholder="e.g. 45 g per meal"
        />
      )}
      <RowTextAreaField
        label="Foods to Avoid"
        value={nutrition.restrictedFoods}
        onChange={set("restrictedFoods")}
        placeholder="List foods patient must avoid (allergens, choking risk, cultural/religious)…"
        className="sm:col-span-2 lg:col-span-3"
        rows={2}
      />
      <RowTextAreaField
        label="Texture / Consistency Restrictions"
        value={nutrition.textureRestrictions}
        onChange={set("textureRestrictions")}
        placeholder="e.g. No nuts, no raw vegetables, thickened liquids only…"
        className="sm:col-span-2 lg:col-span-3"
        rows={2}
      />
      <RowTextAreaField
        label="Restriction Notes"
        value={nutrition.restrictionNotes}
        onChange={set("restrictionNotes")}
        placeholder="Additional dietary or clinical restriction notes for caregivers…"
        className="sm:col-span-2 lg:col-span-3"
        rows={2}
      />
    </div>
  );
};

export const NutritionDietViewFields = ({ patient, bare = false }) => {
  const nutrition = buildNutrition(patient);
  if (!bare && !hasDietData(nutrition)) {
    return (
      <p className="text-sm text-slate-500 italic">No diet information added</p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
      <ViewField label="Diet Type" value={formatDietType(nutrition)} />
      <ViewField label="Food Texture" value={nutrition.foodTexture} />
      <ViewField label="Fluid Management" value={nutrition.fluidManagement} />
      <ViewField label="Meal Assistance" value={nutrition.mealAssistance} />
      <ViewField
        label="Supplement / Enteral"
        value={formatSupplementEnteral(nutrition)}
      />
      <ViewField
        label="Diet Instructions"
        value={nutrition.dietInstructions}
        className="sm:col-span-2 lg:col-span-3"
      />
    </div>
  );
};

export const NutritionRestrictionViewFields = ({ patient, bare = false }) => {
  const nutrition = buildNutrition(patient);
  if (!bare && !hasRestrictionData(nutrition)) {
    return (
      <p className="text-sm text-slate-500 italic">
        No restriction information added
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
      <ViewField
        label="Sodium Restriction"
        value={formatSodiumRestriction(nutrition)}
      />
      <ViewField label="Fluid Restriction" value={nutrition.fluidRestriction} />
      <ViewField
        label="Carbohydrate / Sugar"
        value={formatCarbohydrateRestriction(nutrition)}
      />
      <ViewField
        label="Foods to Avoid"
        value={nutrition.restrictedFoods}
        className="sm:col-span-2 lg:col-span-3"
      />
      <ViewField
        label="Texture / Consistency Restrictions"
        value={nutrition.textureRestrictions}
        className="sm:col-span-2 lg:col-span-3"
      />
      <ViewField
        label="Restriction Notes"
        value={nutrition.restrictionNotes}
        className="sm:col-span-2 lg:col-span-3"
      />
    </div>
  );
};

export const NutritionSubsectionTitle = SubsectionTitle;
